import { useEffect, useState, useMemo, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Trophy, Compass, Users, MapPin, Clock, ArrowRight, Crown, ChevronRight } from 'lucide-react';
import api from '../lib/api';
import { RARITY_UI } from '../lib/rarity';
import RarityBadge from './RarityBadge';
import { useT } from '../i18n/I18nContext';
import MiniExplorerModal from './MiniExplorerModal';

// ---------- SEASONAL BANNER ----------
export function SeasonalBanner() {
  const t = useT();
  const [banner, setBanner] = useState(null);
  useEffect(() => { api.get('/landing/seasonal').then(r => setBanner(r.data.active)).catch(() => {}); }, []);
  if (!banner) return null;
  return (
    <motion.div
      data-testid="seasonal-banner"
      initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      className="relative overflow-hidden rounded-3xl border border-amber-400/25 bg-gradient-to-br from-amber-500/20 via-orange-500/10 to-rose-500/10 p-5"
    >
      <div className="absolute -right-8 -top-8 w-36 h-36 rounded-full bg-amber-400/25 blur-3xl" />
      <div className="relative flex items-start gap-3">
        <span className="text-4xl leading-none">{banner.emoji}</span>
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-[0.25em] text-amber-300">{t('landingBlocks.nowInSeason', 'Now in season')}</p>
          <p className="font-serif text-2xl mt-0.5">{banner.title}</p>
          <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{banner.desc}</p>
        </div>
      </div>
    </motion.div>
  );
}

// ---------- LIVE COMMUNITY DISCOVERY (ticker) ----------
export function LiveCommunityFeed() {
  const nav = useNavigate();
  const t = useT();
  const [items, setItems] = useState([]);
  const [visibleIdx, setVisibleIdx] = useState(0);
  const [profileUserId, setProfileUserId] = useState(null);

  useEffect(() => {
    const load = () => api.get('/landing/community-feed?limit=12').then(r => setItems(r.data.feed || [])).catch(() => {});
    load();
    const tm = setInterval(load, 45_000);
    return () => clearInterval(tm);
  }, []);

  useEffect(() => {
    if (items.length < 2) return;
    const tm = setInterval(() => setVisibleIdx(i => (i + 1) % items.length), 4500);
    return () => clearInterval(tm);
  }, [items.length]);

  if (items.length === 0) return null;
  const cur = items[visibleIdx];
  const ui = RARITY_UI[cur.species_rarity] || RARITY_UI.common;

  return (
    <div className="space-y-2" data-testid="community-feed-block">
      <div className="flex items-center gap-2">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
        </span>
        <p className="text-[10px] uppercase tracking-[0.25em] text-emerald-300">{t('landingBlocks.liveCommunity', 'Live Community Discovery')}</p>
      </div>
      <div className="relative min-h-[92px]">
        <AnimatePresence mode="wait">
          <motion.button
            key={cur.observation_id + visibleIdx}
            type="button"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.5 }}
            onClick={() => cur.user_id ? setProfileUserId(cur.user_id) : nav('/collections')}
            data-testid="community-feed-card"
            className={`w-full text-left rounded-2xl border p-3 flex items-center gap-3 ${ui.cardClass}`}
            style={ui.cardStyle}
          >
            <img
              src={cur.user_picture}
              alt=""
              className="w-11 h-11 rounded-full object-cover ring-2 shrink-0"
              style={{ boxShadow: `0 0 0 2px ${ui.color}44` }}
            />
            <div className="flex-1 min-w-0">
              <p className="text-xs">
                <span className="font-medium">{cur.user_name || t('landingBlocks.explorerFallback', 'Explorer')}</span>
                <span className="text-muted-foreground">{t('landingBlocks.justDiscovered', ' just discovered ')}</span>
              </p>
              <div className="flex items-center gap-2 mt-0.5">
                <RarityBadge rarity={cur.species_rarity || 'common'} size="sm" showParticles={false} />
                <p className="font-serif text-base truncate">{cur.species_common_name}</p>
              </div>
              <p className="text-[10px] text-muted-foreground mt-0.5"><span className="font-mono text-accent">{cur.discovery_score}</span> {t('landingBlocks.discoveryScore', 'Discovery Score')}</p>
            </div>
            {cur.observation_id && (
              <img
                src={`${api.defaults.baseURL}/public/observation-photo/${cur.observation_id}`}
                alt=""
                loading="lazy"
                data-testid="community-feed-species-photo"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                className="w-14 h-14 rounded-xl object-cover shrink-0 ring-1"
                style={{ boxShadow: `0 0 0 1px ${ui.color}55` }}
              />
            )}
            <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
          </motion.button>
        </AnimatePresence>
      </div>
      <AnimatePresence>
        {profileUserId && (
          <MiniExplorerModal userId={profileUserId} onClose={() => setProfileUserId(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------- WORLD FIRST CARD ----------
export function WorldFirstCard() {
  const t = useT();
  const [rec, setRec] = useState(null);
  useEffect(() => { api.get('/landing/latest-world-first').then(r => setRec(r.data && Object.keys(r.data).length ? r.data : null)).catch(() => {}); }, []);
  if (!rec) return null;
  return (
    <motion.div
      data-testid="world-first-card"
      initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      className="relative overflow-hidden rounded-3xl border border-yellow-400/25 bg-gradient-to-br from-yellow-500/15 via-amber-500/10 to-transparent p-5"
    >
      <Trophy className="absolute -right-4 -top-4 w-24 h-24 text-yellow-400/10" strokeWidth={1.5} />
      <div className="relative">
        <p className="text-[10px] uppercase tracking-[0.25em] text-yellow-300 flex items-center gap-1">
          <Trophy className="w-3 h-3" /> {t('landingBlocks.latestWorldFirst', 'Latest World First')}
        </p>
        <p className="font-serif text-xl mt-1">{rec.species_common_name || t('landingBlocks.firstOfKind', 'A first-of-its-kind sighting')}</p>
        <p className="text-xs text-muted-foreground mt-1">
          {rec.region ? `${rec.region} · ` : ''}{t('landingBlocks.discoveredBy', 'Discovered by')} <span className="text-foreground">{rec.user_name || t('landingBlocks.explorerGeneric', 'a WildQuest explorer')}</span>
        </p>
      </div>
    </motion.div>
  );
}

// ---------- PLATFORM STATS (animated counters) ----------
export function PlatformStats() {
  const t = useT();
  const [s, setS] = useState(null);
  useEffect(() => { api.get('/landing/platform').then(r => setS(r.data)).catch(() => {}); }, []);
  if (!s) return null;
  return (
    <div className="grid grid-cols-2 gap-3" data-testid="platform-stats">
      <CounterCard label={t('landingBlocks.explorersToday', 'Explorers today')}    value={s.players_today || 0}         icon={Users} />
      <CounterCard label={t('landingBlocks.speciesToday', 'Species today')}        value={s.species_discovered_today || 0} icon={Compass} />
      <CounterCard label={t('landingBlocks.battlesToday', 'Battles today')}        value={s.battles_today || 0}          icon={Sparkles} />
      <CounterCard label={t('landingBlocks.worldFirstsWeek', 'World firsts wk.')}  value={s.world_firsts_this_week || 0} icon={Crown} />
    </div>
  );
}

function CounterCard({ label, value, icon: Icon }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const dur = 900;
    const from = 0, to = value;
    const step = (t) => {
      const p = Math.min(1, (t - start) / dur);
      setDisplay(Math.round(from + (to - from) * p));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [inView, value]);
  return (
    <div ref={ref} className="rounded-2xl bg-card/60 backdrop-blur border border-white/5 p-3">
      <Icon className="w-3.5 h-3.5 text-accent" strokeWidth={1.75} />
      <p className="font-mono font-semibold text-2xl mt-1 tabular-nums">{display.toLocaleString()}</p>
      <p className="text-[9px] uppercase tracking-widest text-muted-foreground mt-0.5">{label}</p>
    </div>
  );
}

// ---------- FEATURE HIGHLIGHTS ----------
export function FeatureHighlights() {
  const nav = useNavigate();
  const t = useT();
  const items = [
    { icon: '🦉', titleKey: 'landingBlocks.featureAiTitle',       descKey: 'landingBlocks.featureAiDesc',       onClick: () => nav('/discover'), testid: 'feature-ai' },
    { icon: '⚔️', titleKey: 'landingBlocks.featureBattleTitle',    descKey: 'landingBlocks.featureBattleDesc',   onClick: () => nav('/battle'),   testid: 'feature-battle' },
    { icon: '🌿', titleKey: 'landingBlocks.featureCraftingTitle',  descKey: 'landingBlocks.featureCraftingDesc', onClick: () => nav('/alchemy'),  testid: 'feature-crafting' },
  ];
  return (
    <div className="grid grid-cols-1 gap-2" data-testid="feature-highlights">
      {items.map((f, i) => (
        <motion.button
          key={f.titleKey}
          type="button"
          initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ delay: i * 0.05 }}
          whileHover={{ y: -2 }}
          onClick={f.onClick}
          data-testid={f.testid}
          className="w-full text-left rounded-2xl bg-card/60 backdrop-blur border border-white/5 hover:border-accent/40 p-4 flex items-center gap-3 transition-colors"
        >
          <span className="text-3xl leading-none">{f.icon}</span>
          <div className="flex-1 min-w-0">
            <p className="font-serif text-base">{t(f.titleKey)}</p>
            <p className="text-[11px] text-muted-foreground">{t(f.descKey)}</p>
          </div>
          <ArrowRight className="w-4 h-4 text-muted-foreground" />
        </motion.button>
      ))}
    </div>
  );
}

// ---------- PREMIUM CTA ----------
export function PremiumCta() {
  const t = useT();
  return (
    <motion.div
      data-testid="premium-cta"
      initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      className="rounded-3xl bg-gradient-to-br from-indigo-500/15 via-violet-500/10 to-transparent border border-indigo-400/20 p-5"
    >
      <div className="flex items-center gap-2">
        <span className="text-2xl">✨</span>
        <p className="text-[10px] uppercase tracking-[0.25em] text-indigo-300">{t('landingBlocks.premiumBadge', 'WildQuest Pro')}</p>
      </div>
      <p className="font-serif text-lg mt-2">{t('landingBlocks.premiumTitle', 'Go deeper with the naturalists')}</p>
      <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
        <li>{t('landingBlocks.premiumUnlimited', '· Unlimited AI Discoveries')}</li>
        <li>{t('landingBlocks.premiumAdvanced', '· Advanced AI Naturalist — dive into taxonomy, ecology & behavior')}</li>
        <li>{t('landingBlocks.premiumExclusive', '· Exclusive Expeditions & Premium Collectibles (cosmetic-only)')}</li>
      </ul>
      <button
        onClick={() => window.alert(t('landingBlocks.premiumWaitlist', 'WildQuest Pro coming soon — join the waitlist from your Profile.'))}
        className="mt-4 w-full py-2.5 rounded-full bg-white/10 hover:bg-white/15 text-[10px] uppercase tracking-widest border border-white/10"
      >
        {t('landingBlocks.premiumLearnMore', 'Learn more')}
      </button>
    </motion.div>
  );
}

// ---------- Helpers ----------
function timeAgo(iso, t) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return t ? t('landingBlocks.justNow', 'just now') : 'just now';
  if (diff < 3600) {
    const n = Math.floor(diff / 60);
    return t ? t('landingBlocks.minutesAgo', '{n} minutes ago', { n }) : `${n} minutes ago`;
  }
  if (diff < 86400) {
    const n = Math.floor(diff / 3600);
    return t ? t('landingBlocks.hoursAgo', '{n} hours ago', { n }) : `${n} hours ago`;
  }
  return d.toLocaleDateString();
}
